import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthService, CategoriesService, setAuthFailureHandler } from 'services';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [categories, setCategories] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const categoriesRequestIdRef = useRef(0);

    const applyAuthUser = useCallback((nextUser) => {
        const requestId = categoriesRequestIdRef.current + 1;
        categoriesRequestIdRef.current = requestId;

        setUser(nextUser);
        const nextIsAdmin = Boolean(nextUser?.is_admin);
        setIsAdmin(nextIsAdmin);

        if (!nextUser) {
            setCategories([]);
            setIsCategoriesLoading(false);
            return;
        }

        if (!nextIsAdmin) {
            setCategories(nextUser?.categorias || []);
            setIsCategoriesLoading(false);
            return;
        }

        setCategories([]);
        setIsCategoriesLoading(true);
        CategoriesService.getAll().then((result) => {
            if (categoriesRequestIdRef.current !== requestId) {
                return;
            }

            if (result instanceof Error) {
                setCategories([]);
            } else {
                setCategories(result || []);
            }
            setIsCategoriesLoading(false);
        });
    }, []);

    const login = useCallback(
        async (email, password) => {
            const result = await AuthService.auth(email, password);

            if (result instanceof Error) {
                return result.message;
            }

            applyAuthUser(result);
            return undefined;
        },
        [applyAuthUser]
    );

    const logout = useCallback(async () => {
        try {
            await AuthService.logout();
        } finally {
            applyAuthUser(null);
        }
    }, [applyAuthUser]);

    const refreshSession = useCallback(async () => {
        const refreshResult = await AuthService.refresh();

        if (!refreshResult) {
            await logout();
            return false;
        }

        const meResponse = await AuthService.me();

        if (meResponse) {
            applyAuthUser(meResponse);
            return true;
        }

        await logout();
        return false;
    }, [applyAuthUser, logout]);

    useEffect(() => {
        setAuthFailureHandler(() => logout());

        return () => setAuthFailureHandler(null);
    }, [logout]);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const meResponse = await AuthService.me();

                if (meResponse) {
                    applyAuthUser(meResponse);
                } else {
                    applyAuthUser(null);
                }
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, []);

    const isAuthenticated = useMemo(() => !!user, [user]);

    return (
        <AuthContext.Provider
            value={{
                categories,
                isAdmin,
                isCategoriesLoading,
                isAuthenticated,
                isLoading,
                login,
                logout,
                refreshSession,
                user
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => useContext(AuthContext);
